import * as turf from '@turf/turf';
import proj4 from 'proj4';

export class Extent3D {
  constructor(
    public minX: number,
    public minY: number,
    public minZ: number,
    public maxX: number,
    public maxY: number,
    public maxZ: number
  ) {}

  getWidth(): number {
    return this.maxX - this.minX;
  }

  getHeight(): number {
    return this.maxY - this.minY;
  }

  getDepth(): number {
    return this.maxZ - this.minZ;
  }

  getCenter(): [number, number, number] {
    return [
      (this.minX + this.maxX) / 2,
      (this.minY + this.maxY) / 2,
      (this.minZ + this.maxZ) / 2,
    ];
  }

  contains(coord: [number, number, number]): boolean {
    return (
      coord[0] >= this.minX &&
      coord[0] <= this.maxX &&
      coord[1] >= this.minY &&
      coord[1] <= this.maxY &&
      coord[2] >= this.minZ &&
      coord[2] <= this.maxZ
    );
  }

  containsExtent(other: Extent3D): boolean {
    return (
      other.minX >= this.minX &&
      other.maxX <= this.maxX &&
      other.minY >= this.minY &&
      other.maxY <= this.maxY &&
      other.minZ >= this.minZ &&
      other.maxZ <= this.maxZ
    );
  }

  intersects(other: Extent3D): boolean {
    return (
      this.minX <= other.maxX &&
      this.maxX >= other.minX &&
      this.minY <= other.maxY &&
      this.maxY >= other.minY &&
      this.minZ <= other.maxZ &&
      this.maxZ >= other.minZ
    );
  }

  extend(coord: [number, number, number]): Extent3D {
    return new Extent3D(
      Math.min(this.minX, coord[0]),
      Math.min(this.minY, coord[1]),
      Math.min(this.minZ, coord[2]),
      Math.max(this.maxX, coord[0]),
      Math.max(this.maxY, coord[1]),
      Math.max(this.maxZ, coord[2])
    );
  }

  buffer(distance: number): Extent3D {
    return new Extent3D(
      this.minX - distance,
      this.minY - distance,
      this.minZ - distance,
      this.maxX + distance,
      this.maxY + distance,
      this.maxZ + distance
    );
  }

  getIntersection(other: Extent3D): Extent3D | null {
    if (!this.intersects(other)) return null;
    return new Extent3D(
      Math.max(this.minX, other.minX),
      Math.max(this.minY, other.minY),
      Math.max(this.minZ, other.minZ),
      Math.min(this.maxX, other.maxX),
      Math.min(this.maxY, other.maxY),
      Math.min(this.maxZ, other.maxZ)
    );
  }

  isEmpty(): boolean {
    return this.getWidth() <= 0 || this.getHeight() <= 0 || this.getDepth() <= 0;
  }

  clone(): Extent3D {
    return new Extent3D(this.minX, this.minY, this.minZ, this.maxX, this.maxY, this.maxZ);
  }

  getVolume(): number {
    const w = this.getWidth();
    const h = this.getHeight();
    const d = this.getDepth();
    if (w <= 0 || h <= 0 || d <= 0) return 0;
    return w * h * d;
  }
}

export class BoundingBox3D {
  public center: [number, number, number];
  public halfAxes: [number, number, number, number, number, number, number, number, number];

  constructor(center: [number, number, number], halfAxes: number[]) {
    this.center = [...center] as [number, number, number];
    if (halfAxes.length !== 9) {
      throw new Error('halfAxes must be a flat array of 9 numbers representing a 3x3 matrix');
    }
    this.halfAxes = [...halfAxes] as [number, number, number, number, number, number, number, number, number];
  }

  getCorners(): [number, number, number][] {
    const [cx, cy, cz] = this.center;
    const h = this.halfAxes;
    const ax0 = h[0], ax1 = h[1], ax2 = h[2];
    const ay0 = h[3], ay1 = h[4], ay2 = h[5];
    const az0 = h[6], az1 = h[7], az2 = h[8];

    const corners: [number, number, number][] = [];
    for (const sx of [-1, 1]) {
      for (const sy of [-1, 1]) {
        for (const sz of [-1, 1]) {
          corners.push([
            cx + sx * ax0 + sy * ay0 + sz * az0,
            cy + sx * ax1 + sy * ay1 + sz * az1,
            cz + sx * ax2 + sy * ay2 + sz * az2,
          ]);
        }
      }
    }
    return corners;
  }

  intersects(other: BoundingBox3D): boolean {
    const aabbA = this.toExtent3D();
    const aabbB = other.toExtent3D();
    return aabbA.intersects(aabbB);
  }

  containsPoint(point: [number, number, number]): boolean {
    const dx = point[0] - this.center[0];
    const dy = point[1] - this.center[1];
    const dz = point[2] - this.center[2];
    const h = this.halfAxes;

    const axes: [number, number, number][] = [
      [h[0], h[1], h[2]],
      [h[3], h[4], h[5]],
      [h[6], h[7], h[8]],
    ];

    for (const axis of axes) {
      const len = Math.sqrt(axis[0] * axis[0] + axis[1] * axis[1] + axis[2] * axis[2]);
      if (len === 0) continue;
      const nx = axis[0] / len;
      const ny = axis[1] / len;
      const nz = axis[2] / len;
      const proj = Math.abs(dx * nx + dy * ny + dz * nz);
      if (proj > len) return false;
    }
    return true;
  }

  toExtent3D(): Extent3D {
    const corners = this.getCorners();
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const c of corners) {
      if (c[0] < minX) minX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[2] < minZ) minZ = c[2];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] > maxY) maxY = c[1];
      if (c[2] > maxZ) maxZ = c[2];
    }
    return new Extent3D(minX, minY, minZ, maxX, maxY, maxZ);
  }
}

function toFeature(geojson: any): any {
  if (geojson.type === 'Feature') return geojson;
  return { type: 'Feature', properties: {}, geometry: geojson };
}

function toFeatureCollection(geom1: any, geom2: any): any {
  return turf.featureCollection([toFeature(geom1), toFeature(geom2)]);
}

export class GeometryUtils {
  static buffer(geojson: any, distance: number, options?: { segments?: number; units?: string }): any {
    const feature = toFeature(geojson);
    return turf.buffer(feature, distance, {
      units: (options?.units as any) || 'kilometers',
      steps: options?.segments || 64,
    });
  }

  static union(geom1: any, geom2: any): any {
    try {
      return turf.union(toFeatureCollection(geom1, geom2));
    } catch {
      return null;
    }
  }

  static intersection(geom1: any, geom2: any): any | null {
    try {
      return turf.intersect(toFeatureCollection(geom1, geom2));
    } catch {
      return null;
    }
  }

  static difference(geom1: any, geom2: any): any | null {
    try {
      return turf.difference(toFeatureCollection(geom1, geom2));
    } catch {
      return null;
    }
  }

  static symDifference(geom1: any, geom2: any): any | null {
    try {
      const fc = toFeatureCollection(geom1, geom2);
      const diff1 = turf.difference(fc);
      const fcReversed = toFeatureCollection(geom2, geom1);
      const diff2 = turf.difference(fcReversed);
      if (diff1 && diff2) {
        return turf.union(turf.featureCollection([diff1, diff2]));
      }
      return diff1 || diff2 || null;
    } catch {
      return null;
    }
  }

  static clip(geometry: any, extent: any): any | null {
    try {
      let bbox: [number, number, number, number];
      if (Array.isArray(extent)) {
        bbox = extent as [number, number, number, number];
      } else if (extent.minX !== undefined) {
        bbox = [extent.minX, extent.minY, extent.maxX, extent.maxY];
      } else {
        bbox = turf.bbox(toFeature(extent)) as [number, number, number, number];
      }
      const feature = toFeature(geometry);
      return turf.bboxClip(feature, bbox);
    } catch {
      return null;
    }
  }

  static simplify(geometry: any, tolerance: number): any {
    const feature = toFeature(geometry);
    return turf.simplify(feature, { tolerance, highQuality: true });
  }

  static douglasPeucker(coords: [number, number][], tolerance: number): [number, number][] {
    if (coords.length <= 2) return [...coords];

    let maxDist = 0;
    let maxIdx = 0;
    const first = coords[0];
    const last = coords[coords.length - 1];

    for (let i = 1; i < coords.length - 1; i++) {
      const dist = perpendicularDistance(coords[i], first, last);
      if (dist > maxDist) {
        maxDist = dist;
        maxIdx = i;
      }
    }

    if (maxDist > tolerance) {
      const left = GeometryUtils.douglasPeucker(coords.slice(0, maxIdx + 1), tolerance);
      const right = GeometryUtils.douglasPeucker(coords.slice(maxIdx), tolerance);
      return [...left.slice(0, -1), ...right];
    }

    return [first, last];
  }

  static convexHull(geometry: any): any {
    try {
      const fc = geometry.type === 'FeatureCollection' ? geometry : turf.featureCollection([toFeature(geometry)]);
      return turf.convex(fc);
    } catch {
      return null;
    }
  }

  static centroid(geometry: any): any {
    const feature = toFeature(geometry);
    return turf.centroid(feature);
  }

  static pointOnSurface(geometry: any): any {
    const feature = toFeature(geometry);
    return turf.pointOnFeature(feature);
  }

  static area(geometry: any, _projection?: string): number {
    const feature = toFeature(geometry);
    return turf.area(feature);
  }

  static getLength(geometry: any, _projection?: string): number {
    const feature = toFeature(geometry);
    return turf.length(feature, { units: 'kilometers' });
  }

  static distance(point1: [number, number], point2: [number, number], _projection?: string): number {
    return turf.distance(turf.point(point1), turf.point(point2), { units: 'kilometers' });
  }

  static geodesicArea(polygon: any): number {
    const feature = toFeature(polygon);
    return turf.area(feature);
  }

  static geodesicLength(line: any): number {
    const feature = toFeature(line);
    return turf.length(feature, { units: 'kilometers' });
  }

  static within(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanWithin(toFeature(geom1), toFeature(geom2));
    } catch {
      return false;
    }
  }

  static contains(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanContains(toFeature(geom1), toFeature(geom2));
    } catch {
      return false;
    }
  }

  static intersects(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanIntersects(toFeature(geom1), toFeature(geom2));
    } catch {
      return false;
    }
  }

  static disjoint(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanDisjoint(toFeature(geom1), toFeature(geom2));
    } catch {
      return true;
    }
  }

  static touches(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanTouches(toFeature(geom1), toFeature(geom2));
    } catch {
      return false;
    }
  }

  static crosses(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanCrosses(toFeature(geom1), toFeature(geom2));
    } catch {
      return false;
    }
  }

  static overlaps(geom1: any, geom2: any): boolean {
    try {
      return turf.booleanOverlap(toFeature(geom1), toFeature(geom2));
    } catch {
      return false;
    }
  }

  static transform(geometry: any, fromProj: string, toProj: string): any {
    try {
      const geojson = JSON.parse(JSON.stringify(geometry));
      transformCoordinatesInPlace(geojson, fromProj, toProj);
      return geojson;
    } catch {
      return geometry;
    }
  }

  static nearestPoint(geometry: any, point: [number, number]): any {
    try {
      const fc = geometry.type === 'FeatureCollection'
        ? geometry
        : turf.featureCollection([toFeature(geometry)]);
      return turf.nearestPoint(turf.point(point), fc);
    } catch {
      return null;
    }
  }

  static nearestPoints(geom1: any, geom2: any): [[number, number], [number, number]] {
    try {
      const result = turf.nearestPointOnLine(
        toFeature(geom2) as any,
        turf.centroid(toFeature(geom1)) as any
      );
      const centroid1 = turf.centroid(toFeature(geom1));
      const c1 = centroid1.geometry.coordinates as [number, number];
      const c2 = result.geometry.coordinates as [number, number];
      return [c1, c2];
    } catch {
      const c1 = turf.centroid(toFeature(geom1)).geometry.coordinates as [number, number];
      const c2 = turf.centroid(toFeature(geom2)).geometry.coordinates as [number, number];
      return [c1, c2];
    }
  }
}

function perpendicularDistance(
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number {
  const dx = lineEnd[0] - lineStart[0];
  const dy = lineEnd[1] - lineStart[1];
  const lineLenSq = dx * dx + dy * dy;

  if (lineLenSq === 0) {
    const ex = point[0] - lineStart[0];
    const ey = point[1] - lineStart[1];
    return Math.sqrt(ex * ex + ey * ey);
  }

  const num = Math.abs(
    dy * point[0] - dx * point[1] + lineEnd[0] * lineStart[1] - lineEnd[1] * lineStart[0]
  );
  return num / Math.sqrt(lineLenSq);
}

function transformCoordinatesInPlace(geojson: any, fromProj: string, toProj: string): void {
  if (geojson.type === 'FeatureCollection') {
    for (const feature of geojson.features) {
      transformCoordinatesInPlace(feature, fromProj, toProj);
    }
  } else if (geojson.type === 'Feature') {
    transformCoordinatesInPlace(geojson.geometry, fromProj, toProj);
  } else if (geojson.coordinates) {
    geojson.coordinates = transformCoordArray(geojson.coordinates, fromProj, toProj);
  }
}

function transformCoordArray(coords: any, fromProj: string, toProj: string): any {
  if (typeof coords[0] === 'number') {
    const result = proj4(fromProj, toProj, [coords[0], coords[1]]);
    if (coords.length > 2) {
      return [result[0], result[1], coords[2]];
    }
    return result;
  }
  return coords.map((c: any) => transformCoordArray(c, fromProj, toProj));
}
